// Gönderenin telefonunda link önizlemesi hazırlamak (9.32 · D-653).
//
// Görünüm Kısa ya da Büyük kart olduğunda çalışır (D-655'ten beri varsayılan
// Kısa). Açık adreste hiçbir sayfa açılmaz, yani site gönderen hakkında hiçbir
// şey öğrenmez. Kısa ya da Büyük kartta sayfa gönderenin telefonundan bir kez
// okunur — bu, linki kendisinin açmasıyla aynı şeydir. Önizleme şifreli
// konuşmada şifreli mesajın içinde gider; şifresiz konuşmada metinden ayrı bir
// alanda gider ve sunucu onu da görür (D-658). Alıcının telefonu siteye
// hiçbir durumda bağlanmaz.
//
// Çerez gönderilmiyor: telefondaki tek çerez deposu uygulamanınki, ama yine de
// bir siteye kimliğimizi taşıyacak hiçbir şey yollamıyoruz.
import { previewableUrl } from '../utils/links';
import { parsePageMeta } from './html';
import { makeCardImage, makeIcon } from './image';
import type { LinkPreview, LinkView } from './payload';

type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

const TIMEOUT_MS = 8_000;

// Kim olduğumuzu açıkça söylüyoruz. Telefonun varsayılan kimliğiyle ("CFNetwork")
// YouTube gibi siteler, başlığı ve görseli taşımayan mobil sayfaya yönlendiriyor;
// simülatörde önizleme yalnız "YouTube" yazdı. Bu kimlikle tam sayfa geliyor.
// Başka bir uygulamanın (WhatsApp gibi) kimliğine bürünmüyoruz.
export const PREVIEW_USER_AGENT = 'Mozilla/5.0 (compatible; nosubject-link-preview/1.0; +https://nosubject.co)';
const MAX_HTML_BYTES = 3_000_000;
const MAX_IMAGE_BYTES = 3_000_000;

type PageInfo = { title?: string; description?: string; images: string[]; oembed?: string; icon?: string };

async function fetchWithTimeout(fetchImpl: FetchLike, url: string, accept: string): Promise<Response | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetchImpl(url, {
      method: 'GET',
      headers: { Accept: accept, 'User-Agent': PREVIEW_USER_AGENT },
      credentials: 'omit',
      redirect: 'follow',
      signal: controller.signal,
    });
    return res.ok ? res : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function tooLarge(res: Response, limit: number): boolean {
  const length = Number(res.headers.get('content-length') ?? 0);
  return Number.isFinite(length) && length > limit;
}

async function fetchBytes(fetchImpl: FetchLike, url: string): Promise<Uint8Array | null> {
  const res = await fetchWithTimeout(fetchImpl, url, 'image/png,image/jpeg,image/*;q=0.8');
  if (!res || tooLarge(res, MAX_IMAGE_BYTES)) return null;
  try {
    const bytes = new Uint8Array(await res.arrayBuffer());
    return bytes.length > 0 && bytes.length <= MAX_IMAGE_BYTES ? bytes : null;
  } catch {
    return null;
  }
}

async function readPage(fetchImpl: FetchLike, url: string): Promise<PageInfo | null> {
  const res = await fetchWithTimeout(fetchImpl, url, 'text/html,application/xhtml+xml;q=0.9,*/*;q=0.5');
  if (!res || tooLarge(res, MAX_HTML_BYTES)) return null;
  const type = res.headers.get('content-type') ?? '';
  if (type && !/html/i.test(type)) return null;
  let html: string;
  try {
    html = await res.text();
  } catch {
    return null;
  }
  // Göreli adresler yönlendirmeden SONRAKİ sayfaya göre çözülür.
  const meta = parsePageMeta(html, res.url || url);
  let icon: string | undefined;
  // İlk iki aday yeter; her deneme gönderenin beklediği bir ağ isteği.
  for (const candidate of meta.icons.slice(0, 2)) {
    const bytes = await fetchBytes(fetchImpl, candidate);
    icon = bytes ? makeIcon(bytes) ?? undefined : undefined;
    if (icon) break;
  }
  return {
    title: meta.title ?? meta.siteName,
    description: meta.description,
    images: meta.images,
    oembed: meta.oembed,
    icon,
  };
}

// Aynı link için sayfa bir kez okunur: gönderen kısa görünümden büyük karta
// geçerse yeniden istek atılmaz. Hata önbelleğe girmez; tekrar denenebilir.
const pages = new Map<string, Promise<PageInfo | null>>();
const cardImages = new Map<string, Promise<string | null>>();

const MAX_OEMBED_BYTES = 200_000;

/** oEmbed kaydındaki küçük resmin adresi (yalnız https). */
async function oembedThumbnail(fetchImpl: FetchLike, url: string): Promise<string | null> {
  const res = await fetchWithTimeout(fetchImpl, url, 'application/json');
  if (!res || tooLarge(res, MAX_OEMBED_BYTES)) return null;
  try {
    const body = await res.json() as { thumbnail_url?: unknown };
    const thumb = typeof body.thumbnail_url === 'string' ? body.thumbnail_url : '';
    return /^https:\/\/[^/?#\s]+\S*$/i.test(thumb) ? thumb : null;
  } catch {
    return null;
  }
}

/** Kartın görseli: sayfadaki adaylar sırayla (en çok iki), olmazsa oEmbed küçük resmi. */
async function cardImage(fetchImpl: FetchLike, page: PageInfo): Promise<string | null> {
  for (const candidate of page.images.slice(0, 2)) {
    const image = await cached(cardImages, candidate, async () => {
      const bytes = await fetchBytes(fetchImpl, candidate);
      return bytes ? makeCardImage(bytes) : null;
    });
    if (image) return image;
  }
  if (!page.oembed) return null;
  const thumb = await oembedThumbnail(fetchImpl, page.oembed);
  if (!thumb) return null;
  return cached(cardImages, thumb, async () => {
    const bytes = await fetchBytes(fetchImpl, thumb);
    return bytes ? makeCardImage(bytes) : null;
  });
}

function cached<T>(map: Map<string, Promise<T | null>>, key: string, make: () => Promise<T | null>) {
  let pending = map.get(key);
  if (!pending) {
    pending = make().then((value) => {
      if (value === null) map.delete(key);
      return value;
    });
    map.set(key, pending);
  }
  return pending;
}

/**
 * Önizlemeyi hazırla. Hiçbir işe yarar bilgi çıkmazsa (başlık, logo, görsel
 * hiçbiri) null döner ve link açık adres olarak kalır.
 */
export async function buildLinkPreview(
  url: string,
  view: LinkView,
  fetchImpl: FetchLike = fetch,
): Promise<LinkPreview | null> {
  if (!previewableUrl(url)) return null;
  const page = await cached(pages, url, () => readPage(fetchImpl, url));
  if (!page) return null;
  const preview: LinkPreview = { url, view };
  if (page.title) preview.title = page.title;
  if (page.icon) preview.icon = page.icon;
  if (view === 'card') {
    if (page.description) preview.description = page.description;
    const image = await cardImage(fetchImpl, page);
    if (image) preview.image = image;
  }
  return preview.title || preview.icon || preview.image ? preview : null;
}

/** Testler için: önbelleği boşalt. */
export function resetLinkPreviewCache(): void {
  pages.clear();
  cardImages.clear();
}
